import { Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';

interface HeroSectionProps {
  backgroundImage: string;
  heroImage: string;
}

export function HeroSection({ backgroundImage, heroImage }: HeroSectionProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  return (
    <section 
      className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/80" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        {/* Decorative stars */}
        <motion.div
          variants={itemVariants}
          className="flex justify-center gap-2 mb-8"
        >
          <Star className="w-4 h-4 text-accent fill-accent" />
          <Star className="w-4 h-4 text-accent fill-accent" />
          <Star className="w-4 h-4 text-accent fill-accent" />
        </motion.div>

        {/* Main Header */}
        <motion.h1
          variants={itemVariants}
          className="font-heading text-6xl md:text-7xl lg:text-8xl text-primary mb-8 drop-shadow-lg"
        >
          SoulSeer
        </motion.h1>

        {/* Hero Image */}
        <motion.div
          variants={itemVariants}
          className="mb-12 rounded-2xl overflow-hidden gold-glow"
        >
          <img
            src={heroImage}
            alt="SoulSeer mystical experience"
            className="w-full h-auto max-h-96 object-cover"
          />
        </motion.div>

        {/* Tagline */}
        <motion.div variants={itemVariants} className="mb-10">
          <p className="font-body text-2xl md:text-3xl text-foreground mb-4">
            A Community of Gifted Psychics
          </p>
          <p className="font-body text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Connect with authentic spiritual readers for guidance, clarity, and healing. 
            Fair, ethical, and heart-centered readings at your fingertips.
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <button className="bg-primary hover:bg-primary-dark text-background px-8 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/50 flex items-center gap-2 gold-glow">
            <Sparkles className="w-5 h-5" />
            Start a Reading
          </button>
          <button className="border border-primary hover:bg-primary/10 text-primary px-8 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:border-primary-light">
            Explore Readers
          </button>
        </motion.div>

        {/* Decorative bottom stars */}
        <motion.div
          variants={itemVariants}
          className="flex justify-center gap-2 mt-12"
        >
          <Star className="w-3 h-3 text-accent fill-accent opacity-70" />
          <Star className="w-3 h-3 text-accent fill-accent opacity-70" />
          <Star className="w-3 h-3 text-accent fill-accent opacity-70" />
        </motion.div>
      </motion.div>
    </section>
  );
}
